<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="650px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <el-form
      ref="columnForm"
      :model="formData"
      :rules="formRules"
      label-width="120px"
    >
      <el-form-item label="上级栏目" prop="parent_id">
        <el-select v-model="formData.parent_id" placeholder="请选择上级栏目（可为空）" clearable>
          <el-option label="无（一级栏目）" :value="0" />
          <el-option 
            v-for="column in parentColumnList" 
            :key="column.id" 
            :label="column.name" 
            :value="column.id"
          />
        </el-select>
      </el-form-item>
      
      <el-form-item label="栏目名称" prop="name">
        <el-input v-model="formData.name" placeholder="请输入栏目名称" />
      </el-form-item>
      
      <el-form-item label="栏目图标" prop="logo_url">
        <el-upload
          class="icon-uploader"
          :action="uploadAction"
          :headers="uploadHeaders"
          :show-file-list="false"
          :on-success="handleIconSuccess"
          :before-upload="beforeIconUpload"
          accept="image/*"
        >
          <img v-if="formData.logo_url" :src="formData.logo_url" class="icon-image" />
          <i v-else class="el-icon-plus icon-uploader-icon"></i>
        </el-upload>
        <div class="upload-tip">只能上传jpg/png文件，且不超过1MB</div>
      </el-form-item>
      
      <el-form-item label="栏目描述" prop="note">
        <el-input 
          v-model="formData.note" 
          type="textarea" 
          :rows="3"
          placeholder="请输入栏目描述"
        />
      </el-form-item>
    </el-form>
    
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" :loading="submitLoading" @click="handleSubmit">
        确定
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { saveProductColumn } from '@/api/custom-frontend'

export default {
  name: 'ProductColumnEditDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    columnData: {
      type: Object,
      default: null
    },
    appInfo: {
      type: Object,
      required: true
    },
    parentColumnList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      submitLoading: false,
      formData: {
        name: '',
        parent_id: 0,
        logo_url: '',
        note: ''
      },
      formRules: {
        name: [
          { required: true, message: '请输入栏目名称', trigger: 'blur' }
        ],
        logo_url: [
          { required: true, message: '请上传栏目图标', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    
    dialogTitle() {
      return this.columnData ? '编辑产品栏目' : '添加产品栏目'
    },
    
    uploadAction() {
      return process.env.VUE_APP_BASE_API + '/v1/admin/upload/image'
    },
    
    uploadHeaders() {
      return {
        'Authorization': 'Bearer ' + this.$store.getters.token,
        'AppKey': 'BpLnfgDs'
      }
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.initFormData()
      }
    }
  },
  methods: {
    initFormData() {
      if (this.columnData) {
        this.formData = {
          id: this.columnData.id,
          name: this.columnData.name,
          parent_id: this.columnData.parent_id || 0,
          logo_url: this.columnData.logo_url,
          note: this.columnData.note
        }
      } else {
        this.formData = {
          name: '',
          parent_id: 0,
          logo_url: '',
          note: ''
        }
      }
    },
    
    handleIconSuccess(res, file) {
      if (res.err_code === 0) {
        this.formData.logo_url = res.data.url
        this.$message.success('图标上传成功')
      } else {
        this.$message.error(res.err_msg || '图标上传失败')
      }
    },
    
    beforeIconUpload(file) {
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png'
      const isLt1M = file.size / 1024 / 1024 < 1

      if (!isJPG) {
        this.$message.error('上传图标只能是 JPG/PNG 格式!')
      }
      if (!isLt1M) {
        this.$message.error('上传图标大小不能超过 1MB!')
      }
      return isJPG && isLt1M
    },
    
    async handleSubmit() {
      try {
        await this.$refs.columnForm.validate()
        
        this.submitLoading = true
        const submitData = {
          ...this.formData,
          app_id: this.appInfo.id,
          type: 3 // 产品中心栏目配置
        }
        
        const res = await saveProductColumn(submitData)
        if (res && res.err_code === 0) {
          this.$message.success('保存成功')
          this.$emit('refresh')
          this.handleClose()
        } else {
          this.$message.error(res.err_msg || '保存失败')
        }
      } catch (error) {
        console.error('保存产品栏目失败:', error)
        this.$message.error('保存失败')
      } finally {
        this.submitLoading = false
      }
    },
    
    handleClose() {
      this.dialogVisible = false
      this.$refs.columnForm.resetFields()
    }
  }
}
</script>

<style lang="less" scoped>
.icon-uploader {
  .icon-image {
    width: 80px;
    height: 80px;
    display: block;
    object-fit: cover;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
  }
}

/deep/ .icon-uploader .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 80px;
  height: 80px;
  
  &:hover {
    border-color: #409EFF;
  }
}

.icon-uploader-icon {
  font-size: 20px;
  color: #8c939d;
  width: 80px;
  height: 80px;
  line-height: 80px;
  text-align: center;
}

.upload-tip {
  font-size: 12px;
  color: #999;
  margin-top: 5px;
}
</style>